import type { BasketTotalsFields, ApplyVoucherFields, BasketFields } from "../types";

export interface IPriceValue {
  type: number;
}

export interface IPrice {
  key: string;
  id: number;
  name: string;
  currency: string;
  valueType: string;
  value: IPriceValue;
  minimumOrderQuantity: number | null;
  maximumOrderQuantity: number | null;
  purchaseAllowed: boolean;
  userMessage: string;
  scheduleUserMessage: string;
  activeStartDate: string | null;
  activeEndDate: string | null;
  applyVat: boolean;
  vatRate: number;
  vatRateId: number;
  percentageApplicableForVat: number;
  priorityOrder: number;
}

export interface SimplePriceValue extends IPriceValue {
  priceExcVat: number;
}

export interface PriceBandValue {
  quantityLower: number;
  quantityHigher: number;
  priceExcVat: number;
  priceIncVat: number;
}

export interface TieredPriceValue extends IPriceValue {
  bands: PriceBandValue[];
}

export interface VolumePriceValue extends IPriceValue {
  bands: PriceBandValue[];
}

export interface BracketPriceValue extends IPriceValue {
  bands: PriceBandValue[];
}

export function renderPrice ( price: number, currency = "GBP", locale = "en-GB" ) {
  const config = { style: "currency", currency };
  const number = new Intl.NumberFormat( locale, config );
  return number.format( price || 0 );
}

export function getPriceValue ( price: IPrice, quantity = 1 ) {
  let result = null;

  if ( quantity < 0 ) {
    throw new Error( "Quantity must be 0 or greater" );
  }

  switch ( price.value.type ) {
    case 0: {
      result = ( price.value as SimplePriceValue ).priceExcVat * quantity;
      break;
    }

    case 1: {
      const { bands } = price.value as TieredPriceValue;
      for ( let i = 0; i < bands.length; i += 1 ) {
        const band = bands[i];
        if ( quantity >= band.quantityLower
          && ( quantity <= band.quantityHigher || band.quantityHigher === -1 ) ) {
          result = band.priceExcVat * quantity;
          break;
        }
      }

      break;
    }

    case 2: {
      let tieredResult = 0;

      const { bands } = price.value as VolumePriceValue;
      for ( let i = 0; i < bands.length; i += 1 ) {
        const band = bands[i];
        // if we have a quantity greater than this band, add the whole band range
        if ( quantity > band.quantityHigher && band.quantityHigher !== -1 ) {
          tieredResult += ( band.quantityHigher - band.quantityLower ) * band.priceExcVat;
          // else if we have a quantity within this band, add the quantity within this band
        } else if ( quantity >= band.quantityLower
          && ( quantity <= band.quantityHigher || band.quantityHigher === -1 ) ) {
          tieredResult += ( quantity - ( band.quantityLower - 1 ) ) * band.priceExcVat;
        }
      }

      result = tieredResult;

      break;
    }

    case 3: {
      const { bands } = price.value as BracketPriceValue;
      for ( let i = 0; i < bands.length; i += 1 ) {
        const band = bands[i];
        if ( quantity >= band.quantityLower
          && ( quantity <= band.quantityHigher || band.quantityHigher === -1 ) ) {
          result = band.priceExcVat;
          break;
        }
      }

      break;
    }
  }

  return result;
}

/**
 * Build basket total field configuration
 * @param config The external configuration data to use
 */
export function buildBasketTotalsFields ( config?: BasketTotalsFields ): BasketTotalsFields {
  return {
    discountLabel: config?.discountLabel || "Discount",
    totalLabel: config?.totalLabel || "Total",
    shippingLabel: config?.shippingLabel || "Shipping",
    subtotalLabel: config?.subtotalLabel || "Sub Total",
    vatLabel: config?.vatLabel || "VAT",
  };
}

/**
 * Build the apply voucher field configuration
 * @param config The external configuration data ot use
 */
export function buildApplyVoucherFields ( config?: ApplyVoucherFields ): ApplyVoucherFields {
  return {
    applyVoucherButtonLabel: config?.applyVoucherButtonLabel || "Apply",
    codesAppliedLabel: config?.codesAppliedLabel || "Applied",
    removeVoucherModalHeading: config?.removeVoucherModalHeading || "Remove Vouchers",
    removeVoucherModalMessage: config?.removeVoucherModalMessage || "Remove the following vouchers from the order: <br>{0}",
    errorModalHeading: config?.errorModalHeading || "Voucher Error",
    applyVoucherInputPlaceholder: config?.applyVoucherInputPlaceholder || "Enter voucher code",
  };
}

/**
 * Build the basket field configuration
 * @param config The external configuration data to use
 */
export function buildBasketFields ( config?: BasketFields ): BasketFields {
  return {
    emptyBasketLabel: config?.emptyBasketLabel || "No items in basket",
    priceLabel: config?.priceLabel || "Price",
    productLabel: config?.productLabel || "Product",
    quantityLabel: config?.quantityLabel || "Quantity",
    removeButtonLabel: config?.removeButtonLabel || "Remove",
    removeLabel: config?.removeLabel || "Remove",
  };
}
